import { D1_FAMILY_CHILDREN } from "@/lib/d1-family-children.generated";

import { isGonePath, legacyCrawlCanonicalPath } from "@/lib/crawl-policy";
import { REPORT_PLAN_HINDI_TWINS } from "@/lib/report-hindi-twins";

import { D1_SERVED_HI_SLUGS } from "./d1-festival-routes.generated";
import { HI_DYNAMIC_PATH_PATTERNS, HI_LITERAL_PATHS } from "./locale-twins.generated";
import { getLocalePath, HINDI_SLUGS, localeSlugAliases } from "./locales";

/**
 * The exact Hindi twin of an English route — ONE resolver for every surface
 * that links a Hindi page to its counterpart (the language toggle, the Hindi
 * cross-link blocks).
 *
 * `HINDI_SLUGS` answers a narrower question ("which English top-level slugs
 * have a Hindi hub?") and is keyed by the English slug a Hindi DIRECTORY maps
 * back to. Registry slugs that are legacy names ('remedies' → /upay/, 'vastu'
 * → /vastu-shastra/) and nested routes ('shubh-muhurat/today',
 * '/calendar/festival/<id>/') miss it, so on 2026-09-28 the Hindi cross-link
 * block linked 113 English pages whose Hindi twins were live, and 112 English
 * pages did not link their Hindi twin at all. This resolver reads the route
 * the English page actually serves, the hand-authored alias table, and the
 * filesystem-derived Hindi routes (literal and dynamic), and follows the
 * Hindi path through the same legacy-redirect and 410 rules the site serves.
 */

function routeOf(pathOrSlug: string): string {
  return pathOrSlug.split(/[?#]/)[0].replace(/^\/+|\/+$/g, "");
}

/** Hindi route (slug form) that a Hindi page serves for `enRoute`, or null. */
function servedHindiRoute(enRoute: string): string | null {
  const [first, ...rest] = enRoute.split("/");
  const aliased = localeSlugAliases.hi?.[first] ?? first;
  if (rest.length === 0) {
    if (HI_LITERAL_PATHS.has(aliased)) return aliased;
    return (D1_SERVED_HI_SLUGS as readonly string[]).includes(first) ? first : null;
  }
  // A nested Hindi family keeps either the aliased hub name
  // (/lal-kitab/<planet>/ → /hi/lal-kitab-remedies/<planet>/) or the English
  // one.
  for (const hiFirst of new Set([aliased, first])) {
    const candidate = [hiFirst, ...rest].join("/");
    if (HI_LITERAL_PATHS.has(candidate)) return candidate;
    if (
      Object.values(D1_FAMILY_CHILDREN).some((children) =>
        (children as readonly string[]).includes(`hi/${candidate}`)
      )
    )
      return candidate;
    if (HI_DYNAMIC_PATH_PATTERNS.some((pattern) => pattern.test(candidate))) return candidate;
  }
  return null;
}

/**
 * A /reports/<plan>/ product page pairs with a Hindi report landing of another
 * name (/reports/career-report/ ↔ /hi/career-report/), the hreflang pair its
 * metadata and the sitemap already declare. The route scan cannot find it, so
 * until 2026-10-07 the product pages had no हिंदी toggle and Hindi pages linked
 * the English product page.
 */
function reportHindiTwin(route: string): string | null {
  const [section, plan, ...rest] = route.split("/");
  if (section !== "reports" || !plan || rest.length > 0) return null;
  const twin = REPORT_PLAN_HINDI_TWINS[plan];
  return twin && !isGonePath(twin) ? twin : null;
}

/**
 * Canonical path of the live Hindi twin of an English route, or null when no
 * Hindi page serves it. Accepts a registry slug ("remedies", "vastu",
 * "shubh-muhurat/today") or an English path ("/vastu-shastra/kitchen/").
 */
export function hindiTwinPath(enPathOrSlug: string): string | null {
  const route = routeOf(enPathOrSlug);
  if (!route || route === "hi" || route.startsWith("hi/")) return null;
  const reportTwin = reportHindiTwin(route);
  if (reportTwin) return reportTwin;
  const servedRoute = routeOf(legacyCrawlCanonicalPath(`/${route}/`) ?? route);
  for (const candidate of new Set([route, servedRoute])) {
    const hiRoute = servedHindiRoute(candidate);
    if (!hiRoute) continue;
    const hiPath = `/hi/${hiRoute}/`;
    const finalPath = legacyCrawlCanonicalPath(hiPath) ?? hiPath;
    if (!isGonePath(finalPath)) return finalPath;
  }
  return null;
}

/**
 * Where the language toggle on an English page points: the exact Hindi twin
 * when a Hindi page serves it (/calendar/festival/holi/ →
 * /hi/calendar/festival/holi/), else the Hindi hub of the page's section
 * (/nakshatra/ashwini/ → /hi/nakshatra/), else null and the toggle does not
 * render. Until 2026-09-28 nested pages always got the hub, and festival pages
 * got nothing, so 112 English pages never linked their Hindi twin.
 */
export function hindiToggleHref(enPathname: string): string | null {
  // The English home's Hindi counterpart is the Hindi home (src/app/hi/page.tsx).
  if (enPathname.split(/[?#]/)[0] === "/") return legacyCrawlCanonicalPath("/hi/") ?? "/hi/";
  const exact = hindiTwinPath(enPathname);
  if (exact) return exact;
  const slug = routeOf(enPathname).split("/")[0];
  if (!slug || !HINDI_SLUGS.has(slug)) return null;
  const hub = getLocalePath(slug, "hi");
  return legacyCrawlCanonicalPath(hub) ?? hub;
}
